import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Megaphone, Send } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { SmartButton } from '@/components/ui/interactive/SmartButton';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function AdminAnnouncementsPage() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    publish_to: 'all',
    expire_date: undefined as Date | undefined,
    priority: 'normal',
    enable_interactions: false,
    send_in_app: true,
    send_email: false
  });

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      // Get current employee ID using database function
      const { data: employeeId, error: employeeError } = await supabase
        .rpc('get_current_employee_id');

      if (employeeError) {
        console.error('Error getting employee ID:', employeeError);
        toast.error('حدث خطأ في الحصول على بيانات الموظف');
        return;
      }

      if (!employeeId) {
        toast.error('لم يتم العثور على بيانات الموظف. يرجى التأكد من تسجيل الدخول بشكل صحيح.');
        return;
      }

      const { error } = await supabase
        .from('announcements')
        .insert([{
          title: formData.title,
          message: formData.message,
          publish_to: formData.publish_to,
          expire_date: formData.expire_date ? format(formData.expire_date, 'yyyy-MM-dd') : null,
          priority: formData.priority,
          enable_interactions: formData.enable_interactions,
          send_in_app: formData.send_in_app,
          send_email: formData.send_email,
          created_by: employeeId
        }]);

      if (error) throw error;

      toast.success('تم إنشاء الإعلان بنجاح');
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        publish_to: 'all',
        expire_date: undefined,
        priority: 'normal',
        enable_interactions: false,
        send_in_app: true,
        send_email: false
      });
    } catch (error: any) {
      console.error('Error creating announcement:', error);
      toast.error('حدث خطأ أثناء إنشاء الإعلان');
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
          <Megaphone className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">إنشاء إعلان جديد</h1>
          <p className="text-muted-foreground mt-1">
            تواصل مع الموظفين وشاركهم التحديثات المهمة والسياسات والرسائل
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            تفاصيل الإعلان
          </CardTitle>
          <CardDescription>
            املأ النموذج أدناه لإنشاء إعلان جديد للموظفين
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* عنوان الإعلان */}
          <div className="space-y-2">
            <Label htmlFor="title">عنوان الإعلان *</Label>
            <Input
              id="title"
              placeholder="أدخل عنوان الإعلان"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="text-right"
            />
          </div>

          {/* رسالة الإعلان */}
          <div className="space-y-2">
            <Label htmlFor="message">رسالة الإعلان *</Label>
            <Textarea
              id="message"
              placeholder="أدخل تفاصيل الإعلان"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="min-h-[120px] text-right"
            />
          </div>

          {/* نشر لـ */}
          <div className="space-y-2">
            <Label>نشر لـ</Label>
            <Select
              value={formData.publish_to}
              onValueChange={(value) => setFormData({ ...formData, publish_to: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="اختار..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الموظفين</SelectItem>
                <SelectItem value="managers">المدراء فقط</SelectItem>
                <SelectItem value="department">قسم معين</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* تاريخ انتهاء الإعلان */}
          <div className="space-y-2">
            <Label>تاريخ انتهاء الإعلان</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-right font-normal",
                    !formData.expire_date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="ml-2 h-4 w-4" />
                  {formData.expire_date ? (
                    format(formData.expire_date, "PPP", { locale: ar })
                  ) : (
                    <span>اختر التاريخ</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.expire_date}
                  onSelect={(date) => setFormData({ ...formData, expire_date: date })}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* درجة الأهمية */}
          <div className="space-y-3">
            <Label>درجة الأهمية (اختياري)</Label>
            <RadioGroup
              value={formData.priority}
              onValueChange={(value) => setFormData({ ...formData, priority: value })}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2 space-x-reverse">
                <RadioGroupItem value="normal" id="normal" />
                <Label htmlFor="normal">عادي</Label>
              </div>
              <div className="flex items-center space-x-2 space-x-reverse">
                <RadioGroupItem value="urgent" id="urgent" />
                <Label htmlFor="urgent">ضروري</Label>
              </div>
            </RadioGroup>
          </div>

          {/* تفاعل المشاهدين */}
          <div className="flex items-center justify-between">
            <Label htmlFor="interactions">تفاعل المشاهدين</Label>
            <Switch
              id="interactions"
              checked={formData.enable_interactions}
              onCheckedChange={(checked) => setFormData({ ...formData, enable_interactions: checked })}
            />
          </div>

          {/* طرق نشر الإعلان */}
          <div className="space-y-3">
            <Label>طرق نشر الإعلان</Label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="in-app" className="flex items-center gap-2">
                  الإشعارات داخل التطبيق
                </Label>
                <Switch
                  id="in-app"
                  checked={formData.send_in_app}
                  onCheckedChange={(checked) => setFormData({ ...formData, send_in_app: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="email" className="flex items-center gap-2">
                  إرسال عن طريق البريد الإلكتروني
                </Label>
                <Switch
                  id="email"
                  checked={formData.send_email}
                  onCheckedChange={(checked) => setFormData({ ...formData, send_email: checked })}
                />
              </div>
            </div>
          </div>

          {/* زر الإرسال */}
          <div className="pt-4">
            <SmartButton
              onClick={handleSubmit}
              className="w-full"
              variant="default"
              icon={Send}
            >
              إنشاء الإعلان
            </SmartButton>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}